import { Search, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/** En-têtes de colonnes (desktop). */
export function AdminListColumnHeader({ columns, className }) {
  if (!columns?.length) return null;
  return (
    <div
      className={cn(
        "hidden border-b border-slate-200 bg-slate-50/90 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:grid sm:items-center sm:gap-2",
        className,
      )}
      aria-hidden
    >
      {columns.map((col) => (
        <span key={col.key || col.label} className={col.className}>
          {col.label}
        </span>
      ))}
    </div>
  );
}

/** Ligne cliquable avec chevron (toujours à droite, sans retour à la ligne). */
export function AdminListRow({
  onClick,
  testId,
  children,
  gridClassName,
  className,
  showChevron = true,
  /** Boutons d’action (clics isolés, n’ouvrent pas la ligne). */
  actions = null,
  /** Classe grid pour le chevron (ex. `sm:col-start-6` sur listes à 6 colonnes). */
  chevronClassName = "sm:col-start-4 sm:justify-self-end",
}) {
  const innerGrid = gridClassName || "flex flex-nowrap";
  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/25 focus-visible:ring-inset";

  if (actions) {
    return (
      <li
        className={cn("flex min-w-0 items-stretch", className)}
        data-testid={testId ? `${testId}-wrap` : undefined}
      >
        <button
          type="button"
          onClick={onClick}
          className={cn(
            "min-w-0 flex-1 items-center gap-2 px-3 py-3 text-left transition-colors hover:bg-sky-50/60 sm:gap-3 sm:px-4 sm:py-2.5",
            innerGrid,
            focusRing,
          )}
          data-testid={testId}
        >
          {children}
          {showChevron ? (
            <ChevronRight
              className={cn("ml-auto h-4 w-4 shrink-0 text-slate-300 sm:ml-0", chevronClassName)}
              aria-hidden
            />
          ) : null}
        </button>
        <div
          className="flex max-w-[min(100%,20rem)] shrink-0 flex-wrap items-center justify-end gap-1.5 border-l border-slate-100 bg-slate-50/40 px-2 py-2 sm:max-w-none sm:px-3"
          role="group"
          aria-label="Actions"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {actions}
        </div>
      </li>
    );
  }

  return (
    <li className="min-w-0">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "w-full min-w-0 items-center gap-2 px-3 py-3 text-left transition-colors hover:bg-sky-50/60 sm:gap-3 sm:px-4 sm:py-2.5",
          innerGrid,
          focusRing,
          className,
        )}
        data-testid={testId}
      >
        {children}
        {showChevron ? (
          <ChevronRight
            className={cn("ml-auto h-4 w-4 shrink-0 text-slate-300 sm:ml-0", chevronClassName)}
            aria-hidden
          />
        ) : null}
      </button>
    </li>
  );
}

/**
 * Carte liste admin (recherche, compteur, lignes) — même style que l’onglet Utilisateurs.
 */
export default function AdminListShell({
  searchId,
  searchPlaceholder = "Rechercher…",
  searchValue,
  onSearchChange,
  searchAriaLabel,
  count,
  total,
  countNoun = "résultat",
  emptyMessage = "Aucun élément.",
  toolbar = null,
  columns = null,
  columnGridClassName,
  children,
  listTestId,
  minHeightClass = "min-h-[min(52vh,480px)]",
  /** Liste scrollable dans la carte (panneau split admin) — nécessite un parent avec hauteur max. */
  scrollableList = false,
  className,
}) {
  const hasFilter = Boolean(String(searchValue ?? "").trim());
  const noun = count === 1 ? countNoun : `${countNoun}s`;
  const showSearch = searchValue !== undefined && onSearchChange;

  const listInner =
    count === 0 ? (
      <p className="py-10 text-center text-sm text-slate-500">{emptyMessage}</p>
    ) : (
      <div
        className={cn(
          "flex w-full min-w-0 flex-col",
          columns?.length && !scrollableList ? "min-w-[42rem]" : "",
        )}
        data-testid={listTestId}
      >
        <AdminListColumnHeader columns={columns} className={columnGridClassName} />
        <ul className="m-0 list-none divide-y divide-slate-100 p-0" role="list">
          {children}
        </ul>
      </div>
    );

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm",
        scrollableList && "flex h-full min-h-0 max-h-full flex-col",
        className,
      )}
    >
      {(showSearch || toolbar) && (
        <div className="flex shrink-0 flex-col gap-3 border-b border-slate-100 bg-slate-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          {showSearch ? (
            <div className="relative min-w-0 flex-1 sm:max-w-lg">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                aria-hidden
              />
              <Input
                id={searchId}
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={onSearchChange}
                className="h-10 border-slate-200/90 bg-white pl-9 shadow-sm focus-visible:ring-sky-500/25"
                aria-label={searchAriaLabel || searchPlaceholder}
              />
            </div>
          ) : (
            <div className="flex-1" />
          )}
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {typeof count === "number" ? (
              <Badge variant="secondary" className="font-medium tabular-nums text-slate-700">
                {hasFilter && typeof total === "number"
                  ? `${count} / ${total} ${noun}`
                  : `${count} ${noun}`}
              </Badge>
            ) : null}
            {hasFilter ? <span className="text-xs text-slate-500">Filtre actif</span> : null}
            {toolbar}
          </div>
        </div>
      )}

      {scrollableList ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div
            className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-3 sm:px-5 sm:py-4 [-webkit-overflow-scrolling:touch]"
            data-testid={listTestId ? `${listTestId}-scroll` : undefined}
          >
            {listInner}
          </div>
        </div>
      ) : (
        <div className={cn(minHeightClass, "min-w-0 overflow-x-auto p-3 sm:p-4")}>{listInner}</div>
      )}
    </div>
  );
}
